import {
  approveContactRequest,
  fetchContactRequests,
  fetchContacts,
  rejectContactRequest,
  removeContact,
  sendContactRequest,
} from '../api/client';
import type { ContactDto, ContactRequestDto } from '../api/types';

/**
 * Контакты и заявки — одно состояние на всё приложение (вариант A аудита 2026-10-04).
 *
 * <p><b>Зачем общее.</b> Их показывают два экрана сразу: список чатов (плашка «N заявок») и
 * «Новое сообщение» (сами заявки и контакты). Свои копии в каждом экране расходились бы:
 * принял заявку на одном — на другом плашка висит до следующей загрузки. Здесь одна копия,
 * экраны на неё подписаны (`useContacts`).
 *
 * <p><b>Ошибка загрузки не окно.</b> Раньше сбой загрузки контактов показывал диалог на каждом
 * возврате на экран (п. 12 аудита). Теперь ошибка — поле состояния: экран рисует её строкой
 * с «Повторить», а прежние данные остаются на месте.
 */
export type ContactsState = {
  /** Чей это список: после выхода и входа другим аккаунтом старые контакты показывать нельзя. */
  ownerId: string | null;
  contacts: ContactDto[];
  requests: ContactRequestDto[];
  /** «В сети» по событиям сокета — поверх того, что пришло со списком. */
  presence: Record<string, boolean>;
  /** Заявки, отклонение которых ждёт «Отменить» в тосте: на экране их уже нет. */
  hiddenRequestIds: string[];
  loaded: boolean;
  loading: boolean;
  error: unknown;
};

const EMPTY: ContactsState = {
  ownerId: null,
  contacts: [],
  requests: [],
  presence: {},
  hiddenRequestIds: [],
  loaded: false,
  loading: false,
  error: null,
};

let state: ContactsState = EMPTY;
const listeners = new Set<() => void>();
let inFlight: Promise<void> | null = null;

function set(patch: Partial<ContactsState>): void {
  state = { ...state, ...patch };
  listeners.forEach((fn) => fn());
}

export function getContactsState(): ContactsState {
  return state;
}

export function subscribeContacts(fn: () => void): () => void {
  listeners.add(fn);
  return () => {
    listeners.delete(fn);
  };
}

/**
 * Сменился аккаунт — забыть чужое. Повторный вызов с тем же владельцем ничего не делает.
 *
 * <p>Без оповещения подписчиков: вызывается из рендера (`useContacts`), а будить оттуда другие
 * компоненты React запрещает. Подписчиков в этот момент и нет — аккаунт меняется входом, то есть
 * с новым деревом экранов, — а свой рендер `useSyncExternalStore` и так прочитает новое.
 */
export function bindContactsOwner(ownerId: string | null): void {
  if (state.ownerId === ownerId) return;
  inFlight = null;
  state = { ...EMPTY, ownerId };
}

/** Перечитать контакты и заявки. Параллельные вызовы сливаются в один запрос. */
export function refreshContacts(): Promise<void> {
  if (inFlight) return inFlight;
  const owner = state.ownerId;
  set({ loading: true });
  const run = (async () => {
    try {
      const [contacts, requests] = await Promise.all([fetchContacts(), fetchContactRequests()]);
      // Ответ на запрос прошлого аккаунта не должен лечь в список нового.
      if (state.ownerId !== owner) return;
      set({ contacts, requests, loaded: true, error: null });
    } catch (e) {
      if (state.ownerId !== owner) return;
      set({ error: e });
    } finally {
      if (state.ownerId === owner) set({ loading: false });
      inFlight = null;
    }
  })();
  inFlight = run;
  return run;
}

export function setContactPresence(userId: string, online: boolean): void {
  if (state.presence[userId] === online) return;
  set({ presence: { ...state.presence, [userId]: online } });
}

export function isContactOnline(contact: Pick<ContactDto, 'userId' | 'online'>): boolean {
  return state.presence[contact.userId] ?? contact.online === true;
}

/** Принять заявку: сразу убрать её с экрана, затем сверить список с сервером. */
export async function acceptContactRequest(request: ContactRequestDto): Promise<void> {
  await approveContactRequest(request.id);
  set({ requests: state.requests.filter((r) => r.id !== request.id) });
  await refreshContacts();
}

/**
 * Отправить заявку. Строка найденного человека тут же становится «Заявка отправлена» —
 * раньше после «Добавить» стирался весь поиск, и понять, ушла ли заявка, было нельзя (п. 7).
 */
export async function sendContactRequestTo(userId: string): Promise<ContactRequestDto> {
  const created = await sendContactRequest(userId);
  set({ requests: [...state.requests.filter((r) => r.id !== created.id), created] });
  return created;
}

/** Отозвать свою заявку. */
export async function cancelOutgoingRequest(request: ContactRequestDto): Promise<void> {
  await rejectContactRequest(request.id);
  set({ requests: state.requests.filter((r) => r.id !== request.id) });
}

export async function removeContactById(userId: string): Promise<void> {
  await removeContact(userId);
  set({ contacts: state.contacts.filter((c) => c.userId !== userId) });
}

/** Сколько ждать «Отменить», прежде чем отклонение уйдёт на сервер. Совпадает со сроком тоста. */
export const DECLINE_UNDO_MS = 5000;

const pendingDeclines = new Map<string, ReturnType<typeof setTimeout>>();

/**
 * Отклонить входящую заявку с возможностью передумать (п. 8 аудита).
 *
 * <p>Отклонение уходит на сервер не сразу, а когда истечёт время на «Отменить»: отменить уже
 * отклонённую заявку нельзя — она чужая, и вернуть её может только тот, кто её прислал. До тех
 * пор заявка просто спрятана с экрана.
 *
 * @returns отмена — вернуть заявку на экран и ничего не отправлять
 */
export function declineWithUndo(request: ContactRequestDto, onFailed: (error: unknown) => void): () => void {
  const hide = (hidden: boolean) =>
    set({
      hiddenRequestIds: hidden
        ? [...state.hiddenRequestIds.filter((id) => id !== request.id), request.id]
        : state.hiddenRequestIds.filter((id) => id !== request.id),
    });
  hide(true);
  const timer = setTimeout(() => {
    pendingDeclines.delete(request.id);
    rejectContactRequest(request.id)
      .then(() => {
        set({ requests: state.requests.filter((r) => r.id !== request.id) });
        hide(false);
      })
      .catch((e: unknown) => {
        hide(false);
        onFailed(e);
      });
  }, DECLINE_UNDO_MS);
  pendingDeclines.set(request.id, timer);
  return () => {
    const pending = pendingDeclines.get(request.id);
    if (pending == null) return;
    clearTimeout(pending);
    pendingDeclines.delete(request.id);
    hide(false);
  };
}

/** Только для тестов. */
export function resetContactsStoreForTests(): void {
  pendingDeclines.forEach((t) => clearTimeout(t));
  pendingDeclines.clear();
  inFlight = null;
  state = EMPTY;
  listeners.clear();
}
